import { useState, useEffect, useCallback } from "react";
import { notifySuccess } from "src/assets/toastService";
import { Category, Product, StaffMember } from "src/shared/services/interfacev2";
import { patchProduct, putProduct } from "src/shared/services/servicesv2";

interface UseEditProductProps {
  product: Product;
  categories?: Category[];
  staffMembers?: StaffMember[];
  onClose: () => void;
  onUpdate: () => void;
  open: boolean;
}

export const useEditProduct = ({ product, categories: propCategories = [], staffMembers: propStaffMembers = [], onClose, onUpdate, open }: UseEditProductProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>(propCategories);
  const [staffList, setStaffList] = useState<StaffMember[]>(propStaffMembers);
  const [formData, setFormData] = useState({
    productName: product.productName || "",
    arabicName: product.arabicName || "",
    amount: product.amount || "",
    categoryId: product.categoryId || "",
    staffList: [] as string[],
    type: product.type || "",
    displayOrder: product.displayOrder !== undefined && product.displayOrder !== null ? String(product.displayOrder) : "",
    image: null as File | null,
    barcodeId: product.barcodeId || "",
  });
  const [previewImage, setPreviewImage] = useState<string | null>(product.imageUrl || null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [barcodeMode, setBarcodeMode] = useState<boolean>(!!product.barcodeId);
  const [scanMode, setScanMode] = useState<boolean>(false);

  const getCategoryInitials = useCallback((categoryName: string): string => {
    const words = categoryName.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 5).toUpperCase();
    } else {
      return words.map((word) => word[0].toUpperCase()).join("");
    }
  }, []);

  const updateProductNameWithCategory = useCallback(
    (productName: string, categoryId: string): string => {
      if (!categoryId || !categories.length) return productName;

      const selectedCategory = categories.find((cat) => cat._id.$oid === categoryId);
      if (!selectedCategory) return productName;

      const categoryInitials = getCategoryInitials(selectedCategory.name);
      const cleanedName = productName.replace(/\s*\[[^\]]+\]/g, "").trim();

      return `${cleanedName} [${categoryInitials}]`;
    },
    [categories, getCategoryInitials],
  );

  // Helper to get staff names for display
  const getSelectedStaffNames = useCallback(() => {
    return staffList.filter((staff) => formData.staffList.includes(staff._id.$oid)).map((staff) => staff.staff_name || staff.firstName);
  }, [formData.staffList, staffList]);

  useEffect(() => {
    if (propCategories.length > 0) setCategories(propCategories);
    if (propStaffMembers.length > 0) setStaffList(propStaffMembers);
  }, [propCategories, propStaffMembers]);

  useEffect(() => {
    if (!open) return;

    const staffData = propStaffMembers.length > 0 ? propStaffMembers : staffList;
    const productStaffNames: string[] = product.staffList || [];
    const selectedIds = staffData.filter((staff) => productStaffNames.includes(staff.firstName)).map((staff) => staff._id.$oid);

    setFormData({
      productName: product.productName || "",
      arabicName: product.arabicName || "",
      amount: product.amount || "",
      categoryId: product.categoryId || "",
      staffList: selectedIds,
      type: product.type || "",
      displayOrder: product.displayOrder !== undefined && product.displayOrder !== null ? String(product.displayOrder) : "",
      image: null,
      barcodeId: product.barcodeId || "",
    });
    setPreviewImage(product.imageUrl || null);

    // ✅ FIX: Set barcodeMode based on whether product has a barcode
    setBarcodeMode(!!product.barcodeId);
    setScanMode(false);
  }, [open, product, propCategories, propStaffMembers, staffList]);

  useEffect(() => {
    const productStaffNames: string[] = product.staffList || [];
    const currentStaffIds = staffList.filter((staff) => productStaffNames.includes(staff.firstName)).map((staff) => staff._id.$oid);

    const initialDisplayOrder = product.displayOrder !== undefined && product.displayOrder !== null ? String(product.displayOrder) : "";

    const isDisplayOrderChanged = formData.displayOrder.trim() !== "" && formData.displayOrder !== initialDisplayOrder;

    const isOtherFieldsChanged =
      formData.productName.trim() !== (product.productName || "").trim() ||
      formData.arabicName.trim() !== (product.arabicName || "").trim() ||
      formData.amount.toString() !== (product.amount || "").toString() ||
      formData.categoryId !== (product.categoryId || "") ||
      formData.type !== (product.type || "") ||
      formData.image !== null ||
      formData.barcodeId !== (product.barcodeId || "") ||
      JSON.stringify([...formData.staffList].sort()) !== JSON.stringify([...currentStaffIds].sort());

    const hasFormChanges = isDisplayOrderChanged || isOtherFieldsChanged;

    setHasChanges(hasFormChanges);

    // ✅ Amount validation like AddProductModal
    const amountValue = parseFloat(formData.amount);
    const isValid = formData.productName.trim() !== "" && formData.arabicName.trim() !== "" && formData.categoryId.trim() !== "" && !isNaN(amountValue) && amountValue > 0;

    setIsFormValid(isValid);
  }, [formData, product, staffList]);

  const handleInputChange = useCallback(
    (field: string, value: string | string[] | File | null) => {
      setFormData((prev) => {
        const newFormData = { ...prev, [field]: value };

        if (field === "categoryId" && typeof value === "string" && value) {
          const updatedProductName = updateProductNameWithCategory(prev.productName, value);
          newFormData.productName = updatedProductName;
        }

        return newFormData;
      });
    },
    [updateProductNameWithCategory],
  );

  // Barcode handlers - defined AFTER handleInputChange
  const handleToggleScan = useCallback(() => {
    setScanMode((prev) => !prev);
  }, []);

  const handleBarcodeDetected = useCallback(
    (code: string) => {
      handleInputChange("barcodeId", code);
      setBarcodeMode(true); // ✅ Show the barcode
      setScanMode(false);
    },
    [handleInputChange],
  );

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file) {
        const reader = new FileReader();
        reader.onload = () => setPreviewImage(reader.result as string);
        reader.readAsDataURL(file);
        handleInputChange("image", file);
      } else {
        // Clear both the image file and the preview
        setPreviewImage(null);
        handleInputChange("image", null);
      }
    },
    [handleInputChange],
  );

  const handleUpdate = useCallback(async () => {
    if (!isFormValid) return;

    setIsLoading(true);

    try {
      const initialDisplayOrder = product.displayOrder !== undefined && product.displayOrder !== null ? String(product.displayOrder) : "";

      const isDisplayOrderChanged = formData.displayOrder !== initialDisplayOrder;

      const productStaffNames: string[] = product.staffList || [];
      const currentStaffIds = staffList.filter((staff) => productStaffNames.includes(staff.firstName)).map((staff) => staff._id.$oid);

      const isOtherFieldsChanged =
        formData.productName.trim() !== (product.productName || "").trim() ||
        formData.arabicName.trim() !== (product.arabicName || "").trim() ||
        formData.amount.toString() !== (product.amount || "").toString() ||
        formData.categoryId !== (product.categoryId || "") ||
        formData.type !== (product.type || "") ||
        formData.image !== null ||
        formData.barcodeId !== (product.barcodeId || "") ||
        JSON.stringify([...formData.staffList].sort()) !== JSON.stringify([...currentStaffIds].sort());

      if (isOtherFieldsChanged) {
        // Get staff firstNames from selected IDs for API submission
        const selectedStaffNames = staffList.filter((staff) => formData.staffList.includes(staff._id.$oid)).map((staff) => staff.firstName);

        const updateFormData = new FormData();
        updateFormData.append("productName", formData.productName);
        updateFormData.append("amount", formData.amount);
        updateFormData.append("arabicName", formData.arabicName);
        updateFormData.append("categoryId", formData.categoryId);
        updateFormData.append("barcodeId", formData.barcodeId || "");

        if (formData.type) {
          updateFormData.append("type", formData.type);
        }

        // Send as array of staff firstNames
        updateFormData.append("staffList", JSON.stringify(selectedStaffNames));

        if (formData.image) {
          updateFormData.append("image", formData.image);
        }

        await putProduct(product._id.$oid, updateFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (isDisplayOrderChanged && formData.displayOrder !== undefined && formData.displayOrder !== "") {
        const newIndex = Number(formData.displayOrder);
        if (!isNaN(newIndex) && newIndex > 0) {
          await patchProduct(product._id.$oid, { id: product._id.$oid, newIndex });
        }
      }

      notifySuccess("Product updated successfully");
      onClose();
      onUpdate();
    } catch (error: any) {
      console.error("Error updating product:", error);
    } finally {
      setIsLoading(false);
    }
  }, [formData, isFormValid, staffList, product, onUpdate, onClose]);

  const removeEmojis = useCallback((str: string): string => {
    return str.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\u24C2|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDFFF])/g, "");
  }, []);

  const formatAmount = useCallback((value: string): string => {
    if (!value) return "0.00";
    let [integerPart, decimalPart] = value.split(".");
    if (!integerPart) integerPart = "0";
    if (!decimalPart) decimalPart = "00";
    else if (decimalPart.length === 1) decimalPart = decimalPart + "0";
    else if (decimalPart.length > 2) decimalPart = decimalPart.slice(0, 2);
    return `${integerPart}.${decimalPart}`;
  }, []);

  return {
    isLoading,
    categories,
    staffList,
    formData,
    previewImage,
    hasChanges,
    isFormValid,
    handleInputChange,
    handleFileChange,
    handleUpdate,
    removeEmojis,
    formatAmount,
    getCategoryInitials,
    updateProductNameWithCategory,
    getSelectedStaffNames,
    barcodeMode,
    scanMode,
    handleToggleScan,
    handleBarcodeDetected,
  };
};
